import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import { Anchor, Button, NativeInput, Text, UnstyledButton } from '@/shared/ui';
import { canBoostPublication, isErrorStatus, showQueuedProcessingHint } from '@/shared/utils/portal-publish';
import { FF_APP_FACEBOOK_ADS } from '@/shared/utils/feature-flag';
import type { MissingAdSetting } from '@/shared/utils/ad-readiness';
import { AdSetupNotice } from '../ad-dialogs/AdSetupNotice';
import {
  clampAdBudgetReais,
  clampAdDurationDays,
  isBelowMinimumAdBudget,
  MAX_AD_DURATION_DAYS,
  minAdDailyBudgetReais,
  MIN_AD_DURATION_DAYS,
} from '@/shared/utils/ad-dialog-helpers';
import { channelDotClass } from './channel-groups';
import { formatPublicationTimestamp } from './format-publication-timestamp';
import { type PublicationRow } from './types';
import { type TranslateFn } from './publication-row-parts';
import { AdStatusBadges } from './AdStatusBadges';
import {
  FbIcon,
  IgIcon,
} from '@/features/agency-settings-v2/components/FacebookSettingsSection/FacebookSettingsParts';

/**
 * Renders the formatted publish date shown in the row header as short
 * local date + time (e.g. 31/07/26 14:30).
 *
 * @param props.value - ISO date string of when the publication went live.
 * @param props.rowKey - Row key used for the test id.
 * @returns A small tertiary-colored inline date-time label.
 */
export function PublishedDate({ value, rowKey }: { value: string; rowKey: string }) {
  const label = formatPublicationTimestamp(value);
  if (!label) return null;
  return (
    <Text
      as="span"
      className="text-xs text-[var(--text-tertiary)] shrink-0"
      data-testid={`publication-time-${rowKey}`}
    >
      {label}
    </Text>
  );
}

interface MetaProps {
  row: PublicationRow;
  compact?: boolean;
  /** Opens the explanation of a Lead Ad that was requested but not created. */
  onShowAdError?: (row: PublicationRow) => void;
  /** The agency's ad account, so a running Lead Ad can link to Ads Manager. */
  adAccountId?: string | null;
  getStatusLabel: (status: string) => string;
  onRetry?: (row: PublicationRow) => void;
  retrying?: string | null;
  t: TranslateFn;
}

/**
 * Status label with optional inline retry action for failed publications.
 */
function RowStatusLabel({
  row, t, getStatusLabel, onRetry, retrying, compact,
}: MetaProps) {
  if (compact) return null;
  if (isErrorStatus(row.status) && onRetry) {
    return (
      <Text as="span" className="text-xs font-medium shrink-0 text-[var(--text-tertiary)]">
        {t('publications.status.error')}{' '}
        <Button
          type="button"
          variant="none"
          onClick={() => onRetry(row)}
          disabled={retrying === row.key}
          className="p-0 m-0 border-none bg-transparent cursor-pointer font-semibold underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed text-[var(--seed-base)]"
          data-testid={`retry-publication-btn-${row.key}`}
        >
          {retrying === row.key ? '...' : t('publications.status.retry')}
        </Button>
      </Text>
    );
  }
  const statusClass = row.status === 'published'
    ? 'text-[var(--seed-base)]'
    : 'text-[var(--text-tertiary)]';
  return (
    <Text as="span" className={`text-xs font-medium shrink-0 ${statusClass}`}>
      {getStatusLabel(row.status)}
    </Text>
  );
}

/**
 * The i18n key naming a social row's network, or null for a listing portal.
 * A Marketplace listing shares the 'facebook' portal with the feed post, so the
 * row's own label/key is what tells them apart.
 */
function networkNameKey(row: PublicationRow): string | null {
  if (row.portal === 'instagram') return 'publish.portals.instagramLabel';
  if (row.portal !== 'facebook') return null;
  return /marketplace/i.test(`${row.label} ${row.key}`)
    ? 'publish.portals.facebookMarketplaceLabel'
    : 'publish.portals.facebookLabel';
}

/**
 * What identifies a row's channel: the network's logo and its plain name for
 * Facebook and Instagram (the raw portal id, e.g. FACEBOOK_FEED, meant nothing
 * to an agency), a colour dot and the portal label for everything else.
 */
function RowChannel({ row, t }: { row: PublicationRow; t: TranslateFn }) {
  const nameKey = networkNameKey(row);
  if (!nameKey) {
    return (
      <>
        <Text as="span" className={`h-2 w-2 shrink-0 rounded-(--radius-sm) ${channelDotClass(row.portal)}`} aria-hidden="true" />
        <Text as="span" className="text-xs font-semibold text-[var(--text-primary)]">{row.label}</Text>
      </>
    );
  }
  const Glyph = row.portal === 'instagram' ? IgIcon : FbIcon;
  return (
    <>
      <Text
        as="span"
        className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full ${channelDotClass(row.portal)}`}
        aria-hidden="true"
        data-testid={`publication-channel-icon-${row.key}`}
      >
        <Glyph size={12} color="#fff" />
      </Text>
      <Text as="span" className="text-xs font-semibold text-[var(--text-primary)]">{t(nameKey)}</Text>
    </>
  );
}

/**
 * Renders the leading metadata of a publication row: portal label, and — unless
 * compact — the localized status and publish date.
 */
export function RowMeta({ row, compact, getStatusLabel, onRetry, retrying, onShowAdError, adAccountId, t }: MetaProps) {
  return (
    <div className="flex items-center gap-(--space-2) min-w-0 flex-1 flex-wrap">
      <RowChannel row={row} t={t} />
      <RowStatusLabel
        row={row}
        t={t}
        getStatusLabel={getStatusLabel}
        onRetry={onRetry}
        retrying={retrying}
        compact={compact}
      />
      {!compact && row.published_at && <PublishedDate value={row.published_at} rowKey={row.key} />}
      {!compact && <AdStatusBadges row={row} t={t} onShowAdError={onShowAdError} adAccountId={adAccountId} />}
    </div>
  );
}

/**
 * Renders an external "view post" link button that opens the published post in
 * a new tab.
 */
export function ViewPostLink({ url, rowKey, t }: { url: string; rowKey: string; t: TranslateFn }) {
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" underline="never"
      className="px-(--space-2) py-(--space-1) max-md:min-h-9 text-xs font-semibold whitespace-nowrap rounded-(--radius-md) border border-[var(--border-subtle)] flex items-center gap-(--space-1) cursor-pointer hover:opacity-80 transition-opacity text-[var(--text-secondary)] bg-transparent"
      data-testid={`view-post-btn-${rowKey}`}
    >
      <ExternalLinkIcon width={12} height={12} className="shrink-0" />
      {t('publications.viewPost')}
    </Anchor>
  );
}

interface ActionsProps {
  row: PublicationRow;
  canEdit: boolean;
  canBoost: boolean;
  compact?: boolean;
  isConnected: boolean;
  isRemoving: boolean;
  isUpdating: boolean;
  isBoosting: boolean;
  isRetrying: boolean;
  showBoostPanel: boolean;
  onToggleBoost: () => void;
  postUrl: string | null;
  onEdit: (row: PublicationRow) => void;
  onConfirmRemove: (row: PublicationRow) => void;
  t: TranslateFn;
}

/**
 * Renders the row-level action buttons (view, boost, edit, remove).
 */
export function RowActions({
  row, canEdit, canBoost, compact, isConnected, isRemoving, isUpdating,
  isBoosting, isRetrying, showBoostPanel, onToggleBoost, postUrl, onEdit, onConfirmRemove, t,
}: ActionsProps) {
  const busy = isRemoving || isUpdating || isRetrying;
  const disableRemove = !isConnected || busy;
  return (
    <div className="flex flex-wrap items-center gap-(--space-1-5) max-w-full">
      {postUrl && <ViewPostLink url={postUrl} rowKey={row.key} t={t} />}
      {canBoost && !compact && (
        <Button
          type="button"
          variant="none"
          disabled={isBoosting || busy}
          onClick={onToggleBoost}
          className="px-(--space-2) py-(--space-1) max-md:min-h-9 text-xs font-semibold whitespace-nowrap rounded-(--radius-md) border border-[var(--seed-base)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity text-[var(--seed-base)] bg-transparent"
          data-testid={`boost-btn-${row.key}`}
          aria-expanded={showBoostPanel}
        >
          {isBoosting ? '...' : t('publications.actions.boost')}
        </Button>
      )}
      {canEdit && !compact && (
        <Button type="button" variant="none" disabled={busy} onClick={() => onEdit(row)} className="px-(--space-2) py-(--space-1) max-md:min-h-9 text-xs font-semibold whitespace-nowrap rounded-(--radius-md) border border-[var(--seed-base)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity text-[var(--seed-base)] bg-transparent" data-testid={`edit-btn-${row.key}`}>
          {isUpdating ? '...' : t('publications.actions.edit')}
        </Button>
      )}
      {!compact && (
        <Button type="button" variant="none" disabled={disableRemove} onClick={() => isConnected && onConfirmRemove(row)} className="px-(--space-2) py-(--space-1) max-md:min-h-9 text-xs font-semibold whitespace-nowrap rounded-(--radius-md) border border-[var(--status-error-text)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity text-[var(--status-error-text)] bg-transparent" data-testid={`remove-btn-${row.key}`} {...(!isConnected ? { title: t('publications.disconnectedWarning') } : {})}>
          {isRemoving ? '...' : t('publications.actions.remove')}
        </Button>
      )}
    </div>
  );
}

interface BoostPanelProps {
  rowKey: string;
  /** Which portal is being boosted -- picks the Facebook- or Instagram-worded title. */
  portal: string;
  t: TranslateFn;
  budget: number;
  duration: number;
  isBoosting: boolean;
  onBudget: (v: number) => void;
  onDuration: (v: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
  /** What the agency must still fill in before it can boost; the panel then shows only that. */
  missing?: MissingAdSetting[];
  /** Where the agency fills it in. */
  settingsHref?: string;
}

/**
 * Modal to configure and confirm a boost (Impulsionar) on a Facebook or
 * Instagram post -- picks the title copy by `portal`, everything else is
 * shared. Rendered as a centered dialog (not an inline card expansion) so
 * confirming a paid action gets the same focused treatment as other
 * confirmations in the app, and uses the app's own brand color instead of
 * the warning/amber palette -- boosting is a normal paid action, not an
 * error state.
 */
export function BoostPanel({
  rowKey, portal, t, budget, duration, isBoosting, onBudget, onDuration, onCancel, onConfirm,
  missing = [], settingsHref = '',
}: BoostPanelProps) {
  const blocked = missing.length > 0;
  const titleKey = portal === 'instagram' ? 'publications.boost.titleInstagram' : 'publications.boost.title';
  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center" onClick={onCancel}>
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" />
      <div
        className="relative w-full max-w-sm mx-(--space-4) rounded-(--radius-xl) bg-(--surface-primary) border border-(--border-subtle) shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`boost-panel-${rowKey}`}
      >
        <div className="flex items-center justify-between px-(--space-5) py-(--space-4) border-b border-(--border-subtle)">
          <Text className="m-0 text-sm font-semibold text-(--text-primary)">
            {t(titleKey)}
          </Text>
          <UnstyledButton
            onClick={onCancel}
            className="w-6 h-6 flex items-center justify-center rounded-(--radius-md) border-none bg-transparent hover:bg-(--surface-secondary) cursor-pointer text-(--text-tertiary) text-lg"
          >
            &times;
          </UnstyledButton>
        </div>

        {blocked && (
          <div className="px-(--space-5) py-(--space-4)">
            <AdSetupNotice t={t} kind="boost" missing={missing} settingsHref={settingsHref} testId={`boost-setup-${rowKey}`} />
          </div>
        )}

        {!blocked && (
        <div className="px-(--space-5) py-(--space-4) flex gap-(--space-4) flex-wrap">
          <Text as="label" className="flex flex-col gap-(--space-1)">
            <Text as="span" className="text-xs text-(--text-secondary)">
              {t('publications.boost.budgetLabel')}
            </Text>
            <NativeInput
              type="number"
              min={minAdDailyBudgetReais()}
              value={String(budget)}
              onChange={(e) => onBudget(Number(e.target.value))}
              onBlur={() => onBudget(clampAdBudgetReais(budget))}
              className="w-24 px-(--space-2) py-(--space-1-5) text-sm rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary) outline-none transition-colors focus:border-(--seed-base) focus:ring-1 focus:ring-(--seed-base)"
              data-testid={`boost-budget-input-${rowKey}`}
            />
            <Text as="span" className="text-xs text-(--text-tertiary)" data-testid={`boost-budget-min-hint-${rowKey}`}>
              {t('publications.boost.budgetMin', { min: minAdDailyBudgetReais() })}
            </Text>
          </Text>
          <Text as="label" className="flex flex-col gap-(--space-1)">
            <Text as="span" className="text-xs text-(--text-secondary)">
              {t('publications.boost.durationLabel')}
            </Text>
            <NativeInput
              type="number"
              min={MIN_AD_DURATION_DAYS}
              max={MAX_AD_DURATION_DAYS}
              value={String(duration)}
              onChange={(e) => onDuration(Number(e.target.value))}
              onBlur={() => onDuration(clampAdDurationDays(duration))}
              className="w-20 px-(--space-2) py-(--space-1-5) text-sm rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary) outline-none transition-colors focus:border-(--seed-base) focus:ring-1 focus:ring-(--seed-base)"
              data-testid={`boost-duration-input-${rowKey}`}
            />
          </Text>
        </div>
        )}

        <div className="px-(--space-5) py-(--space-3) border-t border-(--border-subtle) flex gap-(--space-2) justify-end">
          <Button
            type="button"
            variant="none"
            disableSize
            onClick={onCancel}
            className="px-(--space-3) py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) border border-(--border-subtle) cursor-pointer hover:opacity-80 text-(--text-secondary) bg-transparent"
          >
            {t('publications.actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={blocked || isBoosting || isBelowMinimumAdBudget(budget) || clampAdDurationDays(duration) !== duration}
            onClick={onConfirm}
            className="px-(--space-3) py-(--space-1-5) text-xs font-semibold rounded-(--radius-md) cursor-pointer disabled:opacity-50 hover:opacity-90 bg-(--seed-base) text-(--surface-primary)"
            data-testid={`boost-confirm-btn-${rowKey}`}
          >
            {t('publications.boost.confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Resolves boost eligibility for a publication row from connection and payment state.
 */
export function resolveCanBoost(
  row: PublicationRow,
  isConnected: boolean,
  fbHasPaymentMethod: boolean,
  tokenExpiresAt?: string | null,
): boolean {
  if (!FF_APP_FACEBOOK_ADS) return false;
  return canBoostPublication({
    portal: row.portal,
    postId: row.post_id,
    adId: row.ad_id,
    isConnected,
    fbHasPaymentMethod,
    tokenExpiresAt,
  });
}

/**
 * Renders a warning banner shown when a portal is no longer connected,
 * indicating that publication actions are unavailable.
 */
export function DisconnectedWarning({ portal, t }: { portal: string; t: TranslateFn }) {
  return (
    <div className="flex items-start gap-(--space-1-5) rounded-(--radius-md) px-(--space-2) py-(--space-1-5) text-xs bg-[color-mix(in_srgb,var(--status-warning)_10%,var(--surface-primary))] text-[var(--status-warning-text)]" data-testid={`disconnected-warning-${portal}`}>
      <ExclamationTriangleIcon width={10} height={10} className="mt-(--space-0-5) shrink-0" />
      <Text as="span">{t('publications.disconnectedWarning')}</Text>
    </div>
  );
}

/**
 * Builds scheduled-row banner copy, including the chosen send time when known.
 */
function scheduledHintText(t: TranslateFn, publishedAt?: string): string {
  if (!publishedAt) return t('publications.scheduledHint');
  return t('publications.scheduledHintAt', {
    when: formatPublicationTimestamp(publishedAt),
  });
}

/**
 * Warning banner shown while a publication is queued or scheduled and not yet live.
 */
export function QueuedProcessingHint({ row, t }: { row: PublicationRow; t: TranslateFn }) {
  if (!showQueuedProcessingHint(row)) return null;
  const message = row.status === 'scheduled'
    ? scheduledHintText(t, row.published_at)
    : t('publications.queuedHint');
  return (
    <div
      className="inline-flex w-fit items-start gap-(--space-1-5) rounded-(--radius-md) px-(--space-2) py-(--space-1-5) text-xs bg-[color-mix(in_srgb,var(--status-warning)_10%,var(--surface-primary))] text-[var(--status-warning-text)]"
      data-testid={`queued-hint-${row.key}`}
    >
      <ExclamationTriangleIcon width={10} height={10} className="mt-(--space-0-5) shrink-0" />
      <Text as="span">{message}</Text>
    </div>
  );
}
